import supabase from '../config/supabase';
import type { PhotoPosition } from '../types/photo';

export const STUDENT_PHOTOS_BUCKET = 'student-photos';

export const STUDENT_BODY_PHOTO_POSITIONS: PhotoPosition[] = [
  'front',
  'back',
  'right',
  'left',
];

export const STUDENT_PROFILE_PHOTO_FILE = 'profile-picture.jpg';
export const STUDENT_BODY_PHOTOS_FOLDER = 'body';
/** Fotos de equipamentos da aluna: student-photos/{id}/equipment/ */
export const STUDENT_EQUIPMENT_PHOTOS_FOLDER = 'equipment';
/** Pasta usada anteriormente pelo portal antes da padronização body/. */
export const LEGACY_STUDENT_BODY_PHOTOS_FOLDER = 'photos';
export const STUDENT_BODY_PHOTO_DATE_FOLDER_REGEX = /^\d{4}-\d{2}-\d{2}$/;
export const BRAZIL_TIME_ZONE = 'America/Sao_Paulo';

/** Nomes antigos usados antes da padronização left/right. */
export const LEGACY_BODY_PHOTO_FILE_ALIASES: Partial<Record<PhotoPosition, string>> = {
  right: 'right_side',
  left: 'left_side',
};

/** Pasta raiz da aluna no bucket: student-photos/{id}/ */
export const buildStudentPhotoFolder = (studentId: number): string => String(studentId);

export const buildStudentPhotoStoragePath = (studentId: number, fileName: string): string => (
  `${buildStudentPhotoFolder(studentId)}/${fileName}`
);

/** Pasta das fotos corporais: student-photos/{id}/body/ */
export const buildStudentBodyPhotosFolder = (studentId: number): string => (
  `${buildStudentPhotoFolder(studentId)}/${STUDENT_BODY_PHOTOS_FOLDER}`
);

/** Pasta das fotos de equipamentos: student-photos/{id}/equipment/ */
export const buildStudentEquipmentPhotosFolder = (studentId: number): string => (
  `${buildStudentPhotoFolder(studentId)}/${STUDENT_EQUIPMENT_PHOTOS_FOLDER}`
);

export const isStudentBodyPhotoAssessmentDateFolder = (value: string): boolean => (
  STUDENT_BODY_PHOTO_DATE_FOLDER_REGEX.test(value.trim())
);

export const getStudentBodyPhotoAssessmentDate = (referenceDate: Date = new Date()): string => (
  referenceDate.toLocaleDateString('en-CA', { timeZone: BRAZIL_TIME_ZONE })
);

export const buildStudentBodyPhotoAssessmentFolder = (
  studentId: number,
  assessmentDate: string,
): string => `${buildStudentBodyPhotosFolder(studentId)}/${assessmentDate}`;

export const buildLegacyPortalBodyPhotosFolder = (studentId: number): string => (
  `${buildStudentPhotoFolder(studentId)}/${LEGACY_STUDENT_BODY_PHOTOS_FOLDER}`
);

export const buildStudentBodyPhotoPath = (
  studentId: number,
  position: PhotoPosition,
  options: { assessmentDate?: string; fileName?: string } = {},
): string => {
  const assessmentDate = options.assessmentDate ?? getStudentBodyPhotoAssessmentDate();
  const fileName = options.fileName ?? `${position}.jpg`;
  return `${buildStudentBodyPhotoAssessmentFolder(studentId, assessmentDate)}/${fileName}`;
};

export const buildLegacyPortalBodyPhotoPath = (
  studentId: number,
  fileName: string,
): string => `${buildLegacyPortalBodyPhotosFolder(studentId)}/${fileName}`;

export const buildStudentProfilePhotoPath = (studentId: number): string => (
  buildStudentPhotoStoragePath(studentId, STUDENT_PROFILE_PHOTO_FILE)
);

/** Fotos corporais salvas direto na pasta da aluna (legado portal). */
export const buildLegacyFlatStudentBodyPhotoPath = (
  studentId: number,
  position: PhotoPosition,
  fileName?: string,
): string => buildStudentPhotoStoragePath(studentId, fileName ?? `${position}.jpg`);

/** Fotos corporais salvas direto em body/ sem subpasta de data (legado). */
export const buildLegacyUndatedStudentBodyPhotoPath = (
  studentId: number,
  position: PhotoPosition,
  fileName?: string,
): string => `${buildStudentBodyPhotosFolder(studentId)}/${fileName ?? `${position}.jpg`}`;

/** Leitura de fotos antigas salvas com GUID e subpasta body/. */
export const buildLegacyStudentBodyPhotoPath = (
  ownerId: string,
  fileName: string,
): string => `${ownerId}/body/${fileName}`;

export const buildLegacyStudentProfilePhotoPath = (ownerId: string): string => (
  `${ownerId}/${STUDENT_PROFILE_PHOTO_FILE}`
);

/** Fotos de perfil de usuárias do portal (staff), fora da pasta da aluna. */
export const buildPortalUserProfilePhotoPath = (userId: string): string => (
  `${userId}/${STUDENT_PROFILE_PHOTO_FILE}`
);

const STUDENT_BODY_PHOTO_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'] as const;
const STUDENT_BODY_PHOTO_IMAGE_FILE_REGEX = /\.(jpe?g|png|webp)$/i;

export const getStudentBodyPhotoFileName = (position: PhotoPosition): string => `${position}.jpg`;

export const isStudentBodyPhotoImageFileName = (fileName: string): boolean => (
  STUDENT_BODY_PHOTO_IMAGE_FILE_REGEX.test(fileName)
);

export const getStudentBodyPhotoFileNamesToResolve = (position: PhotoPosition): string[] => {
  const aliases = [position, LEGACY_BODY_PHOTO_FILE_ALIASES[position]].filter(
    (value): value is string => Boolean(value),
  );

  return aliases.flatMap((alias) => (
    STUDENT_BODY_PHOTO_IMAGE_EXTENSIONS.map((extension) => `${alias}.${extension}`)
  ));
};

/** Aceita `{position}.jpg` e variantes legadas (`front-uuid.png`, `right_side.jpg`). */
export const matchesStudentBodyPhotoPositionFileName = (
  fileName: string,
  position: PhotoPosition,
): boolean => {
  const normalized = fileName.trim().toLowerCase();
  if (!isStudentBodyPhotoImageFileName(normalized)) {
    return false;
  }

  const aliases = [position, LEGACY_BODY_PHOTO_FILE_ALIASES[position]].filter(
    (value): value is string => Boolean(value),
  );

  return aliases.some((alias) => {
    const aliasLower = alias.toLowerCase();
    if (STUDENT_BODY_PHOTO_IMAGE_EXTENSIONS.some((extension) => (
      normalized === `${aliasLower}.${extension}`
    ))) {
      return true;
    }

    return normalized.startsWith(`${aliasLower}-`);
  });
};

export const findStudentBodyPhotoFileNameInSet = (
  fileNames: Iterable<string>,
  position: PhotoPosition,
): string | null => {
  const names = Array.from(fileNames);
  const exactCandidates = new Set(
    getStudentBodyPhotoFileNamesToResolve(position).map((name) => name.toLowerCase()),
  );

  const exactMatch = names.find((name) => exactCandidates.has(name.toLowerCase()));
  if (exactMatch) {
    return exactMatch;
  }

  const prefixMatches = names
    .filter((name) => matchesStudentBodyPhotoPositionFileName(name, position))
    .sort((left, right) => right.localeCompare(left));

  return prefixMatches[0] ?? null;
};

type StorageFolderEntry = {
  name: string;
  metadata?: Record<string, unknown> | null;
};

const listStorageFolderEntries = async (folderPath: string): Promise<StorageFolderEntry[]> => {
  const { data, error } = await supabase.storage
    .from(STUDENT_PHOTOS_BUCKET)
    .list(folderPath);

  if (error || !data) {
    return [];
  }

  return data as StorageFolderEntry[];
};

const listStorageFolderFileNames = async (folderPath: string): Promise<Set<string>> => {
  const entries = await listStorageFolderEntries(folderPath);
  return new Set(
    entries
      .filter((entry) => Boolean(entry.metadata) || isStudentBodyPhotoImageFileName(entry.name))
      .map((entry) => entry.name),
  );
};

export const extractStudentBodyPhotoAssessmentDates = (
  entries: StorageFolderEntry[],
): string[] => (
  entries
    .filter((entry) => (
      isStudentBodyPhotoAssessmentDateFolder(entry.name)
      && !isStudentBodyPhotoImageFileName(entry.name)
    ))
    .map((entry) => entry.name)
    .filter(isStudentBodyPhotoAssessmentDateFolder)
    .sort((left, right) => right.localeCompare(left))
);

export const listStudentBodyPhotoAssessmentDates = async (studentId: number): Promise<string[]> => {
  const entries = await listStorageFolderEntries(buildStudentBodyPhotosFolder(studentId));
  return extractStudentBodyPhotoAssessmentDates(entries);
};

export const getLatestStudentBodyPhotoAssessmentDate = async (
  studentId: number,
): Promise<string | null> => {
  const dates = await listStudentBodyPhotoAssessmentDates(studentId);
  return dates[0] ?? null;
};

export const listStudentBodyPhotoFileNames = async (
  studentId: number,
  assessmentDate?: string,
): Promise<Set<string>> => {
  if (assessmentDate) {
    return listStorageFolderFileNames(buildStudentBodyPhotoAssessmentFolder(studentId, assessmentDate));
  }

  return listStorageFolderFileNames(buildStudentBodyPhotosFolder(studentId));
};

export const listLegacyPortalBodyPhotoFileNames = async (studentId: number): Promise<Set<string>> => (
  listStorageFolderFileNames(buildLegacyPortalBodyPhotosFolder(studentId))
);

export const listStudentRootPhotoFileNames = async (studentId: number): Promise<Set<string>> => (
  listStorageFolderFileNames(buildStudentPhotoFolder(studentId))
);

/** @deprecated Use listStudentBodyPhotoFileNames ou listStudentRootPhotoFileNames. */
export const listStudentPhotoFileNames = listStudentBodyPhotoFileNames;

export const hasStudentPhotoInBucket = async (
  studentId: number,
  fileName: string,
  assessmentDate?: string,
): Promise<boolean> => {
  const fileNames = await listStudentBodyPhotoFileNames(studentId, assessmentDate);
  return fileNames.has(fileName);
};

/** @deprecated Não usar. Bucket student-photos é privado — use getStudentPhotoSignedUrl. */
export const getStudentPhotoPublicUrl = (_storagePath: string): string => {
  throw new Error(
    'getStudentPhotoPublicUrl foi desativado (bucket privado). Use getStudentPhotoSignedUrl.',
  );
};

export const getStudentPhotoSignedUrl = async (
  storagePath: string,
  expiresInSeconds = 60 * 15,
): Promise<string | null> => {
  const { data, error } = await supabase.storage
    .from(STUDENT_PHOTOS_BUCKET)
    .createSignedUrl(storagePath, expiresInSeconds);

  if (error || !data?.signedUrl) {
    return null;
  }

  return data.signedUrl;
};

const EQUIPMENT_IMAGE_FILE_REGEX = /\.(jpe?g|png|webp)$/i;

export interface StudentEquipmentPhoto {
  id: string;
  fileName: string;
  storagePath: string;
  url: string;
  uploadedAt: string | null;
}

/** Lista imagens em student-photos/{id}/equipment/ (somente leitura). */
export const listStudentEquipmentPhotos = async (
  studentId: number,
): Promise<StudentEquipmentPhoto[]> => {
  const folderPath = buildStudentEquipmentPhotosFolder(studentId);
  const { data, error } = await supabase.storage
    .from(STUDENT_PHOTOS_BUCKET)
    .list(folderPath, {
      limit: 200,
      sortBy: { column: 'name', order: 'asc' },
    });

  if (error || !data) {
    return [];
  }

  const imageEntries = data.filter((entry) => (
    Boolean(entry.name)
    && EQUIPMENT_IMAGE_FILE_REGEX.test(entry.name)
    && Boolean(entry.metadata)
  ));

  const photos: StudentEquipmentPhoto[] = [];

  for (const entry of imageEntries) {
    const storagePath = `${folderPath}/${entry.name}`;
    const url = await getStudentPhotoSignedUrl(storagePath);
    if (!url) {
      continue;
    }

    const updatedAt = typeof entry.updated_at === 'string' ? entry.updated_at : null;
    const createdAt = typeof entry.created_at === 'string' ? entry.created_at : null;

    photos.push({
      id: storagePath,
      fileName: entry.name,
      storagePath,
      url,
      uploadedAt: updatedAt || createdAt,
    });
  }

  return photos;
};

export const withPhotoCacheBuster = (url: string, forceRefresh = false): string => {
  if (!forceRefresh) {
    return url;
  }

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}t=${Date.now()}`;
};

export const stripPhotoCacheParam = (url: string): string => url.split('?')[0] ?? url;

export const resolveAvailableStudentPhotoUrl = async (storagePath: string): Promise<string | null> => (
  getStudentPhotoSignedUrl(storagePath)
);

const resolveStudentBodyPhotoUrlFromFolder = async (
  folderPath: string,
  position: PhotoPosition,
  fileNames: Set<string>,
): Promise<string | null> => {
  const matchedFileName = findStudentBodyPhotoFileNameInSet(fileNames, position);
  if (!matchedFileName) {
    return null;
  }

  return getStudentPhotoSignedUrl(`${folderPath}/${matchedFileName}`);
};

const appendResolvedPhotoUrl = (
  url: string,
  options: { bustCache?: boolean },
): string => withPhotoCacheBuster(url, options.bustCache ?? true);

const assessmentDateToUploadedAt = (assessmentDate: string): string => `${assessmentDate}T00:00:00.000Z`;

export const resolveStudentBodyPhotoUrl = async (
  studentId: number,
  position: PhotoPosition,
  legacyUserId?: string | null,
  assessmentDate?: string | null,
  datedFolderFileNames?: Set<string>,
  legacyUndatedFolderFileNames?: Set<string>,
  legacyPortalFolderFileNames?: Set<string>,
  options: { bustCache?: boolean } = {},
): Promise<{ url: string; uploadedAt: string } | null> => {
  const candidateFileNames = getStudentBodyPhotoFileNamesToResolve(position);
  const resolvedAssessmentDate = assessmentDate ?? await getLatestStudentBodyPhotoAssessmentDate(studentId);

  if (resolvedAssessmentDate) {
    const datedFolderPath = buildStudentBodyPhotoAssessmentFolder(studentId, resolvedAssessmentDate);
    const resolvedDatedFolderFileNames = datedFolderFileNames
      ?? await listStudentBodyPhotoFileNames(studentId, resolvedAssessmentDate);

    const datedFolderUrl = await resolveStudentBodyPhotoUrlFromFolder(
      datedFolderPath,
      position,
      resolvedDatedFolderFileNames,
    );

    if (datedFolderUrl) {
      return {
        url: appendResolvedPhotoUrl(datedFolderUrl, options),
        uploadedAt: assessmentDateToUploadedAt(resolvedAssessmentDate),
      };
    }

    // Fallback sem list: tenta caminhos canônicos (bucket público legado / políticas sem list).
    for (const fileName of candidateFileNames) {
      const datedPathUrl = await resolveAvailableStudentPhotoUrl(`${datedFolderPath}/${fileName}`);

      if (datedPathUrl) {
        return {
          url: appendResolvedPhotoUrl(datedPathUrl, options),
          uploadedAt: assessmentDateToUploadedAt(resolvedAssessmentDate),
        };
      }
    }
  }

  const bodyFolderPath = buildStudentBodyPhotosFolder(studentId);
  const resolvedLegacyUndatedFolderFileNames = legacyUndatedFolderFileNames
    ?? await listStudentBodyPhotoFileNames(studentId);

  const legacyUndatedFolderUrl = await resolveStudentBodyPhotoUrlFromFolder(
    bodyFolderPath,
    position,
    resolvedLegacyUndatedFolderFileNames,
  );

  if (legacyUndatedFolderUrl) {
    return {
      url: appendResolvedPhotoUrl(legacyUndatedFolderUrl, options),
      uploadedAt: '',
    };
  }

  for (const fileName of candidateFileNames) {
    const legacyUndatedPathUrl = await resolveAvailableStudentPhotoUrl(
      `${bodyFolderPath}/${fileName}`,
    );

    if (legacyUndatedPathUrl) {
      return {
        url: appendResolvedPhotoUrl(legacyUndatedPathUrl, options),
        uploadedAt: '',
      };
    }
  }

  const resolvedLegacyPortalFolderFileNames = legacyPortalFolderFileNames
    ?? await listLegacyPortalBodyPhotoFileNames(studentId);

  const legacyPortalFolderUrl = await resolveStudentBodyPhotoUrlFromFolder(
    buildLegacyPortalBodyPhotosFolder(studentId),
    position,
    resolvedLegacyPortalFolderFileNames,
  );

  if (legacyPortalFolderUrl) {
    return {
      url: appendResolvedPhotoUrl(legacyPortalFolderUrl, options),
      uploadedAt: '',
    };
  }

  if (!legacyUserId) {
    return null;
  }

  for (const fileName of candidateFileNames) {
    const legacyPathUrl = await resolveAvailableStudentPhotoUrl(
      buildLegacyStudentBodyPhotoPath(legacyUserId, fileName),
    );

    if (legacyPathUrl) {
      return {
        url: appendResolvedPhotoUrl(legacyPathUrl, options),
        uploadedAt: '',
      };
    }
  }

  return null;
};

export const resolveStudentProfilePhotoUrl = async (
  studentId: number,
  options: { legacyUserId?: string | null; forceRefresh?: boolean } = {},
): Promise<string | null> => {
  const { legacyUserId = null, forceRefresh = false } = options;
  const fileNames = await listStudentRootPhotoFileNames(studentId);

  const currentPathUrl = fileNames.has(STUDENT_PROFILE_PHOTO_FILE)
    ? await getStudentPhotoSignedUrl(buildStudentProfilePhotoPath(studentId))
    : null;

  const legacyPathUrl = legacyUserId
    ? await resolveAvailableStudentPhotoUrl(buildLegacyStudentProfilePhotoPath(legacyUserId))
    : null;

  const resolvedUrl = currentPathUrl ?? legacyPathUrl;

  if (!resolvedUrl) {
    return null;
  }

  return forceRefresh ? withPhotoCacheBuster(resolvedUrl, true) : resolvedUrl;
};
